import * as React from "react";
import { Autocomplete, Checkbox, FormControlLabel, MenuItem, TextField } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import ConfirmModal from "../confirm.modal";
import type { ModalComponentProps } from "../../../types/modal.types";
import {
  initPartnerForm,
  partnerPageUrl,
  type PARTNER_IMAGE_TYPE,
  type partnerForm,
} from "../../../lib/apis/dtos/request/partner.request";
import type { PartnerDetailResponse } from "../../../lib/apis/dtos/response/partner.response";
import { createPartner, updatePartner, uploadPartnerImage } from "../../../lib/apis/v1/partner.service";
import { PARTNER_QUERY_KEY, usePartnerStoreList, usePartnerTypeList } from "../../../lib/apis/hooks/partner.hook";
import { getQueryClient } from "../../../config/queryClient.config";
import SuccessSnackbar from "../../ui/snackbar/success.component";
import FailSnackbar from "../../ui/snackbar/fail.component";
import Button from "../../ui/button";

interface PartnerCreateModalProps extends ModalComponentProps {
  /** 넘어오면 수정 모드 */
  partner?: PartnerDetailResponse;
}

const toForm = (partner?: PartnerDetailResponse): partnerForm =>
  partner
    ? {
        partnerName: partner.partnerName,
        activeFrom: partner.activeFrom ?? "",
        activeTo: partner.activeTo ?? "",
        checkActive: partner.checkActive,
        link: partner.link ?? "",
        contactLink: partner.contactLink ?? "",
        partnerType: partner.partnerType ?? "",
        referenceStoreId: partner.referenceStore?.id ?? null,
      }
    : initPartnerForm;

/** 숫자만 받아 YYYY-MM-DD 로 포맷 (branch/expiration.modal 과 동일 규칙) */
const formatDateInput = (raw: string) => {
  const v = raw.replace(/[^0-9]/g, "").slice(0, 8);
  if (v.length <= 4) return v;
  if (v.length <= 6) return `${v.slice(0, 4)}-${v.slice(4)}`;
  return `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6)}`;
};

const isDateValid = (d: string) => d === "" || /^\d{4}-\d{2}-\d{2}$/.test(d);
const inputSx = { height: "40px" };

interface ImageUploadFieldProps {
  label: string;
  imageUrl: string | null;
  disabled?: boolean;
  onUpload: (file: File) => void;
  onDelete: () => void;
}

/** 이미지 업로드 — 수정 모드에서만 사용 (업로드/삭제 즉시 반영) */
function ImageUploadField({ label, imageUrl, disabled = false, onUpload, onDelete }: ImageUploadFieldProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  return (
    <div>
      <p className="text-style-compact-02 pb-2 font-normal text-black/60">{label}</p>
      <div className="flex-row items-center gap-4">
        <div className="flex h-[80px] w-[140px] shrink-0 items-center justify-center overflow-hidden bg-[#F0F3F4]">
          {imageUrl ? (
            <img src={imageUrl} alt={`${label} 미리보기`} className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="text-[12px] text-black/40">미리보기</span>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUpload(file);
            e.target.value = "";
          }}
        />
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          label={imageUrl ? "변경" : "업로드"}
          onClick={() => inputRef.current?.click()}
        />
        {imageUrl && (
          <Button variant="outline-red" size="sm" disabled={disabled} label="삭제" onClick={onDelete} />
        )}
      </div>
    </div>
  );
}

export default function PartnerCreateModal({ id, onClose, partner }: PartnerCreateModalProps) {
  const isEdit = !!partner;
  const [form, setForm] = React.useState<partnerForm>(() => toForm(partner));

  const { data: storeData, isSuccess: isStoreSuccess } = useQuery(usePartnerStoreList());
  const stores = storeData?.result ?? [];
  const selectedStore = stores.find((s) => s.id === form.referenceStoreId) ?? null;

  /** 제휴사 유형 선택지 — 수정 모드에서 현재 값이 목록에 없으면(코드 추가·삭제) 코드 그대로 한 줄 끼워 넣는다 */
  const { data: typeData, isSuccess: isTypeSuccess } = useQuery(usePartnerTypeList());
  const partnerTypes = typeData?.result ?? [];
  const typeOptions =
    form.partnerType && !partnerTypes.some((type) => type.partnerType === form.partnerType)
      ? [...partnerTypes, { partnerType: form.partnerType, partnerTypeName: partner?.partnerTypeName || form.partnerType }]
      : partnerTypes;

  const mutation = useMutation({
    mutationFn: (payload: partnerForm) =>
      isEdit ? updatePartner({ partnerId: String(partner.partnerId), form: payload }) : createPartner(payload),
    onSuccess: (res) => {
      if (res.error) {
        FailSnackbar(res.message || `제휴 업체 ${isEdit ? "수정" : "생성"} 실패`);
        return;
      }
      SuccessSnackbar(`제휴 업체 ${isEdit ? "수정" : "생성"} 완료`);
      getQueryClient().invalidateQueries({ queryKey: [PARTNER_QUERY_KEY.list] });
      getQueryClient().invalidateQueries({ queryKey: [PARTNER_QUERY_KEY.detail] });
      onClose?.();
    },
    onError: () => FailSnackbar(`제휴 업체 ${isEdit ? "수정" : "생성"} 실패`),
  });

  // 업로드 API 가 즉시 반영되므로 응답 URL 로 미리보기를 갱신한다
  const [logoImageUrl, setLogoImageUrl] = React.useState(partner?.logoImageUrl ?? null);
  const [bannerImageUrl, setBannerImageUrl] = React.useState(partner?.bannerImageUrl ?? null);
  const [shareImageUrl, setShareImageUrl] = React.useState(partner?.shareImageUrl ?? null);

  const imageMutation = useMutation({
    mutationFn: uploadPartnerImage,
    onSuccess: (res, variables) => {
      const action = variables.delete ? "삭제" : "업로드";
      if (res.error) {
        FailSnackbar(res.message || `이미지 ${action} 실패`);
        return;
      }
      SuccessSnackbar(`이미지 ${action} 완료`);
      setLogoImageUrl(res.result?.logoImageUrl ?? null);
      setBannerImageUrl(res.result?.bannerImageUrl ?? null);
      setShareImageUrl(res.result?.shareImageUrl ?? null);
      getQueryClient().invalidateQueries({ queryKey: [PARTNER_QUERY_KEY.detail] });
      getQueryClient().invalidateQueries({ queryKey: [PARTNER_QUERY_KEY.list] });
    },
    onError: () => FailSnackbar("이미지 처리 실패"),
  });

  const handleImage = (imageType: PARTNER_IMAGE_TYPE, file?: File) =>
    partner &&
    imageMutation.mutate({
      partnerId: partner.partnerId,
      imageType,
      file,
      delete: !file,
    });

  const changeText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id: key, value } = e.target;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const changeDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id: key, value } = e.target;
    setForm((prev) => ({ ...prev, [key]: formatDateInput(value) }));
  };

  const disabled =
    !form.partnerName.trim() ||
    !form.link.trim() ||
    !form.partnerType ||
    !isDateValid(form.activeFrom) ||
    !isDateValid(form.activeTo) ||
    mutation.isPending;

  return (
    <ConfirmModal
      id={id}
      onClose={onClose}
      className="w-[720px]"
      title={isEdit ? "제휴 업체 수정하기" : "제휴 업체 생성하기"}
      rightButton={{
        title: isEdit ? "제휴 업체 수정하기" : "제휴 업체 생성하기",
        variant: disabled ? "disabled" : "primary",
        disabled,
        onClick: () => mutation.mutate(form),
      }}
    >
      <p className="text-[16px] py-4 text-gray-60">제휴 업체 정보를 입력해주세요.</p>

      <div className="flex flex-col gap-4">
        <div className="flex-row gap-3 items-center">
          <TextField
            id="partnerName"
            className="flex-1"
            label="제휴업체명*"
            placeholder="제휴업체명 입력 (ex. OPGG)"
            value={form.partnerName}
            onChange={changeText}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            id="partnerType"
            select
            className="w-[260px] shrink-0"
            label="제휴 유형*"
            value={form.partnerType}
            onChange={(e) => setForm((prev) => ({ ...prev, partnerType: e.target.value }))}
            disabled={!isTypeSuccess}
            slotProps={{
              inputLabel: { shrink: true },
              select: {
                MenuProps: { sx: { zIndex: 9999 } },
                // 표시명만 보여준다 (코드값은 운영자가 알 필요 없음)
                renderValue: (value) =>
                  typeOptions.find((type) => type.partnerType === value)?.partnerTypeName ?? String(value),
              },
            }}
          >
            {typeOptions.length === 0 && (
              <MenuItem value="" disabled>
                {isTypeSuccess ? "유형 목록 없음" : "불러오는 중"}
              </MenuItem>
            )}
            {typeOptions.map((type) => (
              <MenuItem key={type.partnerType} value={type.partnerType}>
                {type.partnerTypeName}
              </MenuItem>
            ))}
          </TextField>
        </div>
        {form.partnerType === "REFERENCE" && (
          <p className="-mt-2 text-[12px] text-black/60">참고 지점은 공통 비교용 백업 제휴사로 쓰입니다.</p>
        )}
        {/* 유형 도입 전에 만든 업체는 유형이 비어 있다 — 저장하려면 한 번 골라야 한다 */}
        {isEdit && !form.partnerType && isTypeSuccess && (
          <p className="-mt-2 text-[12px] text-[#D32F2F]">제휴 유형이 지정되지 않은 업체입니다. 유형을 선택해야 저장할 수 있습니다.</p>
        )}
        <Autocomplete
          options={stores}
          loading={!isStoreSuccess}
          value={selectedStore}
          getOptionLabel={(option) => option.storeName}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, store) => setForm((prev) => ({ ...prev, referenceStoreId: store?.id ?? null }))}
          disablePortal
          slotProps={{ popper: { sx: { zIndex: 9999 } } }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.storeName}
              {option.storeAreaName && <span className="ml-1 text-black/50">({option.storeAreaName})</span>}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="공급업체"
              placeholder="지점명으로 검색"
              helperText={
                isEdit && partner.referenceStore && !selectedStore && isStoreSuccess
                  ? `현재 연결: ${partner.referenceStore.name} (목록에 없어 선택 해제 상태로 보입니다)`
                  : " "
              }
              slotProps={{ inputLabel: { shrink: true } }}
            />
          )}
        />
        <TextField
          id="link"
          label="연결 링크*"
          placeholder="영문/숫자 path 입력 (ex. moongcheap)"
          value={form.link}
          onChange={(e) => setForm((prev) => ({ ...prev, link: e.target.value.replace(/[^a-zA-Z0-9-_]/g, "") }))}
          helperText={form.link ? partnerPageUrl(form.link, form.partnerType || null) : " "}
          slotProps={{ inputLabel: { shrink: true } }}
        />

        <div className="flex-row gap-3 items-center">
          <TextField
            id="activeFrom"
            label="활성화 시작일"
            placeholder="YYYY-MM-DD"
            value={form.activeFrom}
            onChange={changeDate}
            inputProps={{ maxLength: 10 }}
            slotProps={{ inputLabel: { shrink: true }, input: { sx: inputSx } }}
          />
          <span>~</span>
          <TextField
            id="activeTo"
            label="활성화 종료일"
            placeholder="YYYY-MM-DD"
            value={form.activeTo}
            onChange={changeDate}
            inputProps={{ maxLength: 10 }}
            slotProps={{ inputLabel: { shrink: true }, input: { sx: inputSx } }}
          />
          <FormControlLabel
            className="ml-2 whitespace-nowrap"
            label="활성화"
            control={
              <Checkbox checked={form.checkActive} onChange={(e) => setForm((prev) => ({ ...prev, checkActive: e.target.checked }))} />
            }
          />
        </div>

        <TextField
          id="contactLink"
          label="상담 링크"
          placeholder="상담 링크 입력 (ex. 카톡 채널 URL)"
          value={form.contactLink}
          onChange={changeText}
          slotProps={{ inputLabel: { shrink: true } }}
        />

        {isEdit ? (
          <React.Fragment>
            <div className="grid grid-cols-2 gap-4">
              <ImageUploadField
                label="로고 이미지"
                imageUrl={logoImageUrl}
                disabled={imageMutation.isPending}
                onUpload={(file) => handleImage("PROFILE", file)}
                onDelete={() => handleImage("PROFILE")}
              />
              <ImageUploadField
                label="카카오톡 공유 이미지"
                imageUrl={shareImageUrl}
                disabled={imageMutation.isPending}
                onUpload={(file) => handleImage("SHARE", file)}
                onDelete={() => handleImage("SHARE")}
              />
            </div>
            <ImageUploadField
              label="배너 이미지"
              imageUrl={bannerImageUrl}
              disabled={imageMutation.isPending}
              onUpload={(file) => handleImage("BANNER", file)}
              onDelete={() => handleImage("BANNER")}
            />
          </React.Fragment>
        ) : (
          <p className="text-style-compact-04 font-normal text-black/50">
            *로고·배너·카카오톡 공유 이미지는 생성 후 수정에서 업로드할 수 있습니다.
          </p>
        )}
      </div>
    </ConfirmModal>
  );
}
